import { logger } from "@/utils/logger";

import { MessagePart } from './types';
import { hasVehicleProperties } from './vehicleDetection';

export const parseFormattedContent = (content: string): MessagePart[] => {
  const parts: MessagePart[] = [];
  
  // CRITICAL: First check if content contains a Reply_Message JSON block
  // This handles cases where AI outputs text followed by JSON on the same logical block
  const replyMessageIndex = content.indexOf('{"Reply_Message"');
  const replyMessageIndex2 = content.indexOf('{ "Reply_Message"');
  const jsonBlockStart = replyMessageIndex !== -1 ? replyMessageIndex : replyMessageIndex2;
  
  if (jsonBlockStart !== -1) {
    // There's a Reply_Message JSON block - extract and handle separately
    const textBefore = content.substring(0, jsonBlockStart).trim();
    const jsonAndAfter = content.substring(jsonBlockStart);
    
    // Try to extract just the JSON portion using brace counting
    let braceCount = 0;
    let inString = false;
    let escapeNext = false;
    let jsonEndPos = 0;
    
    for (let i = 0; i < jsonAndAfter.length; i++) {
      const char = jsonAndAfter[i];
      
      if (escapeNext) {
        escapeNext = false;
        continue;
      }
      
      if (char === '\\') {
        escapeNext = true;
        continue;
      }
      
      if (char === '"') {
        inString = !inString;
        continue;
      }
      
      if (!inString) {
        if (char === '{') braceCount++;
        if (char === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEndPos = i + 1;
            break;
          }
        }
      }
    }
    
    if (jsonEndPos > 0) {
      const jsonContent = jsonAndAfter.substring(0, jsonEndPos);
      
      try {
        const jsonObj = JSON.parse(jsonContent);
        
        // DON'T add textBefore as a separate text part - it's duplicated from the JSON Intro
        // The JSON contains the proper structured content, so we should only use that
        logger.log('🔧 parseFormattedContent: Found Reply_Message JSON block, discarding', textBefore.length, 'chars of preceding text (likely duplicate of Intro)');
        
        // Return just the JSON object as a vehicle/structured content
        // The caller (messageParserOrchestrator) should handle this properly
        // But since we're in parseFormattedContent, we can't call parseJSONMessage
        // So we'll return an empty array to let the orchestrator handle it properly
        logger.log('🔧 parseFormattedContent: Returning empty to let orchestrator re-parse the extracted JSON');
        return [];
      } catch (e) {
        console.error('❌ parseFormattedContent: Failed to parse Reply_Message JSON:', e);
        // Fall through to regular line-by-line parsing
      }
    }
  }
  
  // PERFORMANCE: Limit line processing for very large content
  const lines = content.split('\n');
  const maxLines = Math.min(lines.length, 100); // Process max 100 lines to prevent blocking
  
  let currentTextBlock = '';
  
  for (let i = 0; i < maxLines; i++) {
    const line = lines[i].trim();
    
    // Skip empty lines
    if (!line) {
      continue;
    }
    
    // PERFORMANCE: Quick check if line could be JSON before expensive parsing
    if ((line.startsWith('{') && line.endsWith('}')) || 
        (line.startsWith('[') && line.endsWith(']'))) {
      
      try {
        const jsonObj = JSON.parse(line);
        
        // If we have accumulated text, add it as a text part
        if (currentTextBlock.trim()) {
          parts.push({ type: 'text', content: currentTextBlock.trim() });
          currentTextBlock = '';
        }
        
        // Check what type of JSON object this is
        if (hasVehicleProperties(jsonObj)) {
          parts.push({ type: 'vehicle', content: jsonObj });
        } else if (jsonObj.similar_vehicles) {
          parts.push({ type: 'similar_vehicles', content: jsonObj });
        } else if (jsonObj.reply_options && Array.isArray(jsonObj.reply_options)) {
          parts.push({ type: 'reply_options', content: jsonObj });
        } else if (jsonObj.type === 'inventory_count' && jsonObj.data) {
          parts.push({ type: 'inventory_count', content: jsonObj.data });
        } else if (Array.isArray(jsonObj) && jsonObj.length > 0 && jsonObj[0].Category !== undefined && jsonObj[0].Count !== undefined) {
          parts.push({ type: 'inventory_count', content: jsonObj });
        }
        
      } catch (error) {
        // Not valid JSON, accumulate as text
        if (currentTextBlock) {
          currentTextBlock += '\n' + line;
        } else {
          currentTextBlock = line;
        }
      }
    } else {
      // Not JSON structure, accumulate as text
      if (currentTextBlock) {
        currentTextBlock += '\n' + line;
      } else {
        currentTextBlock = line;
      }
    }
  }
  
  // If we hit the line limit, add remaining content as text
  if (maxLines < lines.length) {
    const remainingLines = lines.slice(maxLines).join('\n');
    if (remainingLines.trim()) {
      if (currentTextBlock) {
        currentTextBlock += '\n' + remainingLines;
      } else {
        currentTextBlock = remainingLines;
      }
    }
  }
  
  // Add any remaining text
  if (currentTextBlock.trim()) {
    parts.push({ type: 'text', content: currentTextBlock.trim() });
  }
  
  // Reduce logging for performance
  if (parts.length < 10) {
    logger.log('*** FORMATTED CONTENT PARSING RESULT:', parts.map(p => ({ type: p.type, hasContent: !!p.content })));
  } else {
    logger.log('*** FORMATTED CONTENT PARSING RESULT: Found', parts.length, 'parts');
  }
  
  return parts;
};
